/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality. 
 */

import { CustomSubmitClaim, handleProcessClaim } from "../customSubmitClaim";
import {
    transformTransactionKeys,
    disablePaperClaimContactValidation,
    isPaperClaim,
} from "../utils";

/**
 * Validates a form.
 * @param {Object} options - The options object
 * @param {Event|string} options.event - The event object
 * @param {CustomSubmitClaim} options.ctx - The current context
 * @returns {void}
 */
export function validateForm({ event, ctx }) {
    const numAccounts = ctx.getFormInput('number_of_accounts');
    const numTransactions = ctx.getFormInput('number_of_transactions');

    if (event === 'init_claim') {
        // Hide confirmation email alert
        const confirmationEmailAlert = document.querySelector('#confirmation-email-alert');
        confirmationEmailAlert.classList.add('d-none');

        // Reorder form fields
        const beneficialOwnerField = ctx.getFormInput('benefit_of_beneficial_owner').parentElement;
        const fullNameField = ctx.getFormInput('full_name').parentElement;
        fullNameField.after(beneficialOwnerField);

        // Adjust capacity of person field
        const capacityOfPersonField = ctx.getFormInput('capacity_of_person').parentElement;
        capacityOfPersonField.classList.add('col-md-3');
        
        // Adjust signature and date fields
        const signatureField = ctx.getFormInput('signature').parentElement;
        signatureField.classList.remove('col-md-5');
        signatureField.classList.add('col-md-4', 'mb-3');
        
        const dateField = ctx.getFormInput('date').parentElement;
        dateField.classList.remove('col-md-5');
        dateField.classList.add('col-md-3', 'mb-3');
        
        signatureField.after(capacityOfPersonField);
        capacityOfPersonField.after(dateField);

        // move number of accounts to beneficial owner fieldset
        ctx.supportingDocumentation['owner'].container.querySelector('p:nth-child(2)').after(numAccounts.parentElement);

        // move number of transactions to transaction fieldset
        ctx.supportingDocumentation['transaction'].container.querySelector('p:nth-child(2)').after(numTransactions.parentElement);

        // Hide claim fields section
        const claimFields = document.querySelector('#claim-fields');
        claimFields.classList.add('d-none');

        // Move general instructions into claim deadline fieldset
        const claimDeadlineFieldset = document.querySelector('#claimFormDeadline > fieldset');
        const generalInstructions = document.querySelector('#generalInstructionsWrapper');
        claimDeadlineFieldset.appendChild(generalInstructions);
        // Paper claims: phone number and email address are optional (WP-1926)
        if (isPaperClaim()) disablePaperClaimContactValidation();
    }

    ctx.supportingDocumentation['documentation'].setRequired(!ctx.supportingDocumentation['documentation'].hasUploadedFiles);
    ctx.supportingDocumentation['owner'].setRequired(numAccounts.value > 0 && !ctx.supportingDocumentation['owner'].hasUploadedFiles);
    ctx.supportingDocumentation['transaction'].setRequired(numTransactions.value > 0 && !ctx.supportingDocumentation['transaction'].hasUploadedFiles);
}

/**
 * Processes a claim for a custom member.
 * @param {CustomSubmitClaim} ctx - The claim to be processed
 * @returns {Object} The processed claim
 */
export function customProcessClaim(ctx) {
    const claim = handleProcessClaim.call(ctx, validateForm);

    // Prefix transaction keys with section.table and strip parenthetical text
    const prefixedTransactions = transformTransactionKeys(claim.data.transactions || {});

    return {
        ...claim,
        data: {
            ...claim.data,
            contact: {
                ...claim.data.contact,
                'ag_PAPER': window.location.search.includes('paper_claim=true')
            },
            fields: {
                ...claim.data.fields,
                'Claim Form Type': 'Third-Party',
                'Claim Form Status': 'Complete'
            },
            transactions: prefixedTransactions
        }
    };
}

/**
 * Removes event listeners from number inputs when the form is unloaded.
 * @param {CustomSubmitClaim} ctx - The current context
 */
export function onFormUnloaded(ctx) {
    console.log('[Form Unloaded]');
}